import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PlatformApiError, platformApiRequest } from '../api/client.ts';
import { BrandWordmark } from '../components/BrandWordmark.tsx';
import { hasPermission, useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { Button, Chip, Icon } from '../ui/index.ts';
import type { TenantOrgPermission } from '@nadoutong/contracts';

/**
 * Nxtpath tenant app chrome — sidebar + header + main area. Renders the
 * authenticated experience for every tenant-facing page under the
 * root route tree (see `App.tsx`).
 *
 * Visual contract: DESIGN.md (Field Notes, epic #950). The sidebar is
 * the ledger's contents page: same `canvas` paper as the main area,
 * separated by a hairline — layers by lines, not fills (§1.1). The
 * 56px header is a thin toolbar; page titles belong to each page's
 * serif `PageHeader`, the header only echoes the section name.
 */
interface NavItem {
  readonly to: string;
  readonly labelKey: I18nKey;
  readonly testId: string;
  readonly icon: string;
  readonly permission?: TenantOrgPermission;
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/dashboard', labelKey: 'shell.nav.dashboard', testId: 'dashboard', icon: 'dashboard' },
  {
    to: '/keys',
    labelKey: 'shell.nav.keys',
    testId: 'keys',
    icon: 'vpn_key',
    permission: 'member:api_keys:manage',
  },
  {
    to: '/usage',
    labelKey: 'shell.nav.usage',
    testId: 'usage',
    icon: 'analytics',
    permission: 'member:usage:read',
  },
  {
    to: '/organization',
    labelKey: 'shell.nav.organization',
    testId: 'organization',
    icon: 'account_tree',
    permission: 'org:members:read',
  },
  {
    to: '/requests',
    labelKey: 'shell.nav.requests',
    testId: 'requests',
    icon: 'receipt_long',
    permission: 'member:requests:read',
  },
  {
    to: '/optimization',
    labelKey: 'shell.nav.optimization',
    testId: 'optimization',
    icon: 'query_stats',
    permission: 'member:usage:read',
  },
  { to: '/pricing', labelKey: 'shell.nav.pricing', testId: 'pricing', icon: 'sell' },
  {
    to: '/billing',
    labelKey: 'shell.nav.billing',
    testId: 'billing',
    icon: 'payments',
    permission: 'org:billing:read',
  },
  {
    to: '/settings',
    labelKey: 'shell.nav.settings',
    testId: 'settings',
    icon: 'settings',
    permission: 'org:members:read',
  },
];

interface Props {
  readonly children?: ReactNode;
}

export function Shell({ children }: Props): JSX.Element {
  const { user, clearUser, fetchImpl } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();
  const visibleNavItems = useMemo(
    () => NAV_ITEMS.filter((item) => item.permission === undefined || hasPermission(user, item.permission)),
    [user],
  );

  const pageTitleKey = useMemo<I18nKey>(() => {
    const match = visibleNavItems.find((item) => location.pathname.startsWith(item.to));
    return match?.labelKey ?? 'shell.page.console_fallback';
  }, [location.pathname, visibleNavItems]);

  async function handleLogout(): Promise<void> {
    try {
      await platformApiRequest<{ ok: boolean }>('/v1/auth/logout', { method: 'POST' }, fetchImpl);
    } catch (err) {
      // Even if /logout fails we clear the client-side mirror so the UI
      // doesn't stay in an inconsistent "logged in" state. The cookie
      // will expire on its own TTL regardless.
      if (!(err instanceof PlatformApiError)) {
        throw err;
      }
    }
    clearUser();
    navigate('/login', { replace: true });
  }

  const displayEmail =
    user?.email !== undefined && user.email.length > 0 ? user.email : t('shell.session.signed_in');
  const isVerified = user?.emailVerified === true;
  const verifiedBadge = isVerified ? t('shell.session.verified') : t('shell.session.unverified');

  return (
    <div className="flex h-screen overflow-hidden bg-canvas text-ink">
      <aside
        data-testid="shell-sidebar"
        className="flex min-h-0 w-64 shrink-0 flex-col border-r border-line bg-canvas px-4 py-6"
      >
        <div className="mb-8 shrink-0 px-2">
          <BrandWordmark descriptor={t('shell.brand.subtitle')} testId="shell-brand-wordmark" />
          <p className="mt-4 border-l border-line pl-3 text-caption text-ink-low">
            {t('shell.brand.supporting')}
          </p>
        </div>
        <nav
          aria-label={t('shell.nav.aria_label')}
          className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto overscroll-contain"
        >
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`shell-nav-${item.testId}`}
              className={({ isActive }) =>
                [
                  'flex items-center gap-3 rounded px-3 py-2 text-label transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40',
                  isActive
                    ? 'bg-forest-tint text-forest-deep'
                    : 'text-ink-mid hover:bg-active/50 hover:text-ink',
                ].join(' ')
              }
            >
              <Icon name={item.icon} size="sm" />
              <span>{t(item.labelKey)}</span>
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto flex shrink-0 flex-col gap-3 pt-6">
          <div className="flex flex-col gap-1.5 rounded-lg border border-line bg-card px-3 py-3">
            <span className="truncate text-caption font-medium text-ink" data-testid="shell-email">
              {displayEmail}
            </span>
            <Chip
              tone={isVerified ? 'ok' : 'warn'}
              lamp={false}
              className="w-fit"
              testId="shell-verified"
            >
              {verifiedBadge}
            </Chip>
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              void handleLogout();
            }}
            data-testid="shell-logout"
            className="w-full"
          >
            <Icon name="logout" size="sm" />
            <span>{t('shell.logout')}</span>
          </Button>
        </div>
      </aside>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center justify-between border-b border-line bg-canvas px-8">
          <h1 className="text-heading text-ink" data-testid="shell-page-title">
            {t(pageTitleKey)}
          </h1>
          <Chip tone="neutral" lamp>
            Nxtpath tenant
          </Chip>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-8">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}
